import {
	computeContextMenuTitle,
	ContextMenu,
	Pagination,
	SearchInputForm,
	TableView,
	TableViewNoResultConfig,
	useAlertDialog,
	useContextMenu
} from "client/Components";
import { contains } from "client/Data/searching";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useForceRerender,
	useSelection,
	useSortHandler,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	getOffice365UsersWhichDoNotExistInTheWorkspace,
	IImportUserOutcome,
	ImportTarget,
	ImportUserResult,
	importUsersToWorkspace,
	Office365User,
	ResultType,
	UserImportModel,
	getViewModel
} from "client/Api/ImportUsers/importUsers";
import {
	ActionAndOthers,
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { HttpRequestError } from "client/Api/httpClient";
import {
	FunctionComponent,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { userManagementRouting } from "./UserManagementPage";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	connectToRouter
} from "client/Navigation";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type ImportUserPageProps = {
	onNavigate: NavigationCallback;
};

export const ImportUserPage = (props: ImportUserPageProps): ReactElement => {
	const {
		onNavigate
	} = props;

	const [importTarget, setImportTarget] = useState<ImportTarget>(ImportTarget.No);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [items, setItems] = useState<readonly GridItem[]>([]);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [selectedItems, setSelectedItems] = useState<readonly GridItem[]>([]);
	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const importResultsRef: RefObject<readonly ImportUserResult[] | null> = useRef<readonly ImportUserResult[]>(null);

	const [refreshHandler, dataVersion] = useForceRerender();

	const [sortingState, setSortingState] = useState<DataSourceSortableState<GridItem>>(
		{ propertyName: "firstName", direction: DataSourceSortDirection.Ascending }
	);

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [alertDialog, openAlertDialog] = useAlertDialog();

	useActiveSettingsMenuItem(SettingsPageKind.UserManagement);

	const { sortingFor } = useSortHandler<GridItem>(sortingState, setSortingState);

	const { currentItems, pagination } = useOffice365UserDataSource(
		items,
		{
			sorting: sortingState,
			pagination: {
				currentPageNumber,
				amountPerPage: 100
			},
			searchTerm
		}
	);

	const { multiSelection, selectionFor } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems,
			hasMemorization: true,
			allItems: items
		}
	);

	const [contextMenuHandler, contextMenuProps] = useContextMenu(tableRef);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(dataVersion);

			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);
				try {
					const importTarget: ImportTarget = await getViewModel(signal)
						.catch(handlePageDataLoadingError);
					const users: readonly Office365User[] = await getOffice365UsersWhichDoNotExistInTheWorkspace(signal);

					const gridItems: readonly GridItem[] = convertToGridItems(
						users,
						importResultsRef.current
					);

					importResultsRef.current = null;

					setItems(gridItems);
					setImportTarget(importTarget);
					setSelectedItems([]);
					setIsBusy(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_ErrorDuringLoadingOfOffice365Users"));
					setIsBusy(false);
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager, dataVersion]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(currentPageNumber);
			hasImplicitDependencyOn(sortingState);

			tableRef.current
				?.scrollTo(0, 0);
		},
		[
			currentPageNumber,
			sortingState,
			searchTerm
		]
	);

	const importHandler = useCallback(
		async (selection: readonly GridItem[]): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			let importFunction: (batch: readonly UserImportModel[], signal: AbortSignal) => Promise<readonly ImportUserResult[]>;
			switch (importTarget) {
				case ImportTarget.Workspace365:
					importFunction = importUsersToWorkspace;
					break;
				case ImportTarget.No:
					throw new Error("unhandled import target " + importTarget);
				default:
					never(importTarget);
			}

			const unsuccessfulResults: ImportUserResult[] = [];
			try {
				setItems(
					(x) => x.map(x => ({ ...x, importAlertMessage: undefined }))
				);

				const itemCountPerBatch = 500;
				const numberOfBatches: number = Math.ceil(selection.length / itemCountPerBatch);

				for (let i = 0; i < numberOfBatches; i++) {
					const batch: readonly UserImportModel[] = selection.slice(i * itemCountPerBatch, (i + 1) * itemCountPerBatch)
						.map(
							(x): UserImportModel => ({
								firstName: x.firstName,
								lastName: x.lastName,
								emailAddress: x.primaryEmailAddress,
								userPrincipalName: x.userPrincipalName,
								mobilePhone: x.phoneNumber,
								jobTitle: x.title
							})
						);

					const importResult: readonly ImportUserResult[] = await importFunction(batch, globalCancellation);

					unsuccessfulResults.push(
						...importResult.filter(x => x.outcome.type !== ResultType.Success)
					);
				}

				if (unsuccessfulResults.length > 0) {
					importResultsRef.current = unsuccessfulResults;
				}

				refreshHandler();
				setIsBusy(false);

			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				let errorMessage: string | null = null;

				if (e instanceof HttpRequestError) {
					try {
						const responseObject: IUserFriendlyExceptionMessage = e.readJson<IUserFriendlyExceptionMessage>();
						if (responseObject && responseObject.message) {
							errorMessage = responseObject.message;
						}
					} catch (e) {
						console.error();
					}
				}

				openAlertDialog({
					title: localize("_Error"),
					confirmText: localize("_Close"),
					children: errorMessage ?? localize("_ErrorDuringImport"),
					trackingId: "import-o365-user-error-dialog"
				});

				if (unsuccessfulResults.length > 0) {
					importResultsRef.current = unsuccessfulResults;
				}

				refreshHandler();
			}
		},
		[
			globalCancellation,
			importTarget,
			isBusy,
			openAlertDialog,
			refreshHandler
		]
	);

	const backHandler = useCallback(
		(): void => {
			onNavigate(userManagementRouting.overview);
		},
		[onNavigate]
	);

	const handlers = useMemo(
		(): ActionHandlers => {
			return {
				import: importHandler,
				refresh: refreshHandler
			};
		},
		[
			importHandler,
			refreshHandler
		]
	);

	const selectionActions = useMemo(
		(): readonly ImportUserAction[] => {
			return getSelectionActions({
				deselectHandler: () => setSelectedItems([])
			});
		},
		[]
	);

	const defaultActions = useMemo(
		(): readonly ImportUserAction[] => listDefaultActions(handlers),
		[handlers]
	);

	const contextualActions = useMemo(
		(): readonly ImportUserAction[] => {
			return listContextualActions(handlers);
		},
		[handlers]
	);

	const actionContext = useMemo(
		(): ImportUserActionContext => ({
			selection: selectedItems,
			isBusy,
			isMobile
		}),
		[
			isBusy,
			isMobile,
			selectedItems
		]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly ImportUserAction[] => {
			return buildActionsForTopBar<ImportUserActionContext, ImportUserAction>(
				{
					selectionActions,
					contextualActions,
					defaultActions,
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			selectionActions,
			contextualActions,
			defaultActions,
			hasSelection,
			isResponsive
		]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig | undefined => {
			if (isBusy) {
				return undefined;
			}

			if (searchTerm) {
				return {

					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "user",
				title: localize("_NoOffice365UsersAvailable"),
				message: localize("_NoOffice365UsersAvailableToImport")
			};
		},
		[searchTerm, isBusy]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				actions={topBarActions}
				actionContext={actionContext}
				hasSelection={hasSelection}
				title={localize("_ImportOffice365Users")}
				onBack={backHandler}
			>
				<SettingsPage.SearchSection>
					<SearchInputForm onChange={setSearchTerm} />
				</SettingsPage.SearchSection>
				<SettingsPage.Section height="stretch">
					<TableView
						multiSelectable={multiSelection}
						trackingId="import-users-grid"
						items={currentItems}
						itemKey="id"
						columns={[
							{
								title: localize("_FirstName"),
								sortable: sortingFor("firstName"),
								trackingId: "first-name"
							},
							{
								title: localize("_LastName"),
								sortable: sortingFor("lastName"),
								trackingId: "last-name"
							},
							{
								title: localize("_EmailAddress"),
								sortable: sortingFor("primaryEmailAddress"),
								trackingId: "primary-email-address"
							},
							{
								title: localize("_Username"),
								sortable: sortingFor("userPrincipalName"),
								trackingId: "user-principal-name"
							},
							{
								title: localize("_ImportAlertMessage"),
								sortable: sortingFor("importAlertMessage"),
								trackingId: "import-alert-message"
							}
						]}
						ref={tableRef}
						height="stretch"
						noResultConfig={noResultConfig}
					>
						{item => {
							const {
								firstName,
								lastName,
								primaryEmailAddress,
								userPrincipalName,
								importAlertMessage
							} = item;

							return (
								<TableView.Row
									selectable={selectionFor(item)}
									onContextMenu={contextMenuHandler}
									trackingId={userPrincipalName}
								>
									<TableView.Cell tooltip={firstName}>{firstName}</TableView.Cell>
									<TableView.Cell tooltip={lastName}>{lastName}</TableView.Cell>
									<TableView.Cell tooltip={primaryEmailAddress ?? undefined}>
										{primaryEmailAddress}
									</TableView.Cell>
									<TableView.Cell tooltip={userPrincipalName}>{userPrincipalName}</TableView.Cell>
									<TableView.Cell>{importAlertMessage}</TableView.Cell>
								</TableView.Row>
							);
						}}
					</TableView>
					<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
					{contextMenuProps && (
						<ContextMenu
							{...contextMenuProps}
							actions={contextualActions}
							context={actionContext}
							trackingId="import-user-context-menu"
							title={computeContextMenuTitle(
								actionContext.selection,
								(x) => x.userPrincipalName
							)}
						/>
					)}
				</SettingsPage.Section>
				<AiAssistantFabSpacing />
			</SettingsPage>
			{alertDialog}
		</>
	);
};
ImportUserPage.displayName = "ImportUserPage";

export const ImportUserPageForRouter: FunctionComponent = connectToRouter(
	ImportUserPage,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

type GridItem = Office365User & { id: Guid; importAlertMessage?: string | nil; };

function searchMatchFunction(
	item: GridItem,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	const {
		firstName,
		lastName,
		userPrincipalName,
		primaryEmailAddress
	} = item;

	return contains(firstName, normalizedNotEmptySearchTerm)
		|| contains(lastName, normalizedNotEmptySearchTerm)
		|| contains(userPrincipalName, normalizedNotEmptySearchTerm)
		|| contains(primaryEmailAddress ?? "", normalizedNotEmptySearchTerm);
}

const useOffice365UserDataSource = createLocalDataSource<GridItem>()
	.setConfiguration({
		search: { doesMatchItem: searchMatchFunction },
		hasPagination: true
	})
	.getHook();

type ImportUserActionContext = {
	selection: readonly GridItem[];
	isBusy: boolean;
	isMobile: boolean;
};

type ImportUserAction = ActionAndOthers<ImportUserActionContext>;

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(
	handlers: SelectionActionsHandlers
): readonly ImportUserAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

type ActionHandlers = {
	refresh: () => void;
	import: (selection: readonly GridItem[]) => void;
};

function listDefaultActions(handlers: ActionHandlers): readonly ImportUserAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "refresh",
			title: localize("_Refresh"),
			canExecute: (c) => !c.isBusy && c.selection.length === 0,
			execute: () => handlers.refresh(),
			icon: "arrow-rotate-left"
		}
	];
}

function listContextualActions(handlers: ActionHandlers): readonly ImportUserAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "import",
			title: localize("_Import"),
			canExecute: (c) => c.selection.length > 0,
			execute: (c) => handlers.import(c.selection),
			icon: "cloud-arrow-down"
		}
	];
}

function convertToGridItems(
	users: readonly Office365User[],
	importResults: readonly ImportUserResult[] | null
): readonly GridItem[] {
	const gridItems: readonly GridItem[] = users.map(
		(x): GridItem => ({ ...x, id: newGuid() })
	);

	for (const result of importResults ?? []) {
		const userPrincipalName: string = result.userPrincipalName;

		const gridItem: GridItem | undefined = gridItems.find(x => x.userPrincipalName === userPrincipalName);

		if (!gridItem) {
			continue;
		}

		let alertMessage: string | undefined;

		const outcome: IImportUserOutcome = result.outcome;
		switch (outcome.type) {
			case ResultType.Success:
				break;
			case ResultType.Warning:
				alertMessage = outcome.message;
				break;
			case ResultType.Error:
				const validationMessages: readonly string[] = outcome.errors
					.map(
						(validationError): string => {
							if (validationError.propertyName && validationError.propertyName !== "") {
								return validationError.propertyName + " : " + validationError.errorMessage;
							}
							return validationError.errorMessage;
						}
					);

				alertMessage = validationMessages.join(", ");

				break;
			default:
				never(outcome);
		}

		gridItem.importAlertMessage = alertMessage;
	}

	return gridItems;
}

export const importUsersRouting = {
	page: "/ImportUsers",
	fromOffice365GroupsPage: "/ImportUsers/ImportFromOffice365Groups"
};
